import React, { CSSProperties } from 'react';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

const EMAIL_LINK_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer'
};

const headingId = (node: any): string => {
  const collect = (n: any): string => {
    if (!n) return '';
    if (typeof n.value === 'string') return n.value;
    if (Array.isArray(n.content)) return n.content.map(collect).join('');
    return '';
  };
  return collect(node)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
};

export const replacePlaceholder = (
  body: any,
  textToReplace: string,
  replacement: string
) => {
  if (typeof body === 'string' || body instanceof String) {
    return body.replace(new RegExp(textToReplace, 'g'), replacement);
  }

  return JSON.parse(
    JSON.stringify(body).replace(new RegExp(textToReplace, 'g'), replacement)
  );
};

export default function jsonToString(
  text: any,
  classes?: any,
  styles?: CSSProperties,
  linkAnalytics?: any
) {
  const getImage = (n: any) => {
    if (
      n &&
      n.data &&
      n.data.target &&
      n.data.target.sys &&
      n.data.target.sys.id
    ) {
      // TODO: Use NextJS image to retrieve embedeed richtext images using id
      // Take as reference the implementation on ecommerce-ui
      return <></>;
    }
    return <></>;
  };

  const renderNode = {
    [BLOCKS.HEADING_1]: (n: any, children: any) => (
      <h1
        id={headingId(n)}
        key={`heading1-${children}`}
        className={classes ? classes.header : ''}
        style={styles}
      >
        {children}
      </h1>
    ),
    [BLOCKS.HEADING_2]: (n: any, children: any) => (
      <h2
        id={headingId(n)}
        key={`heading2-${children}`}
        className={classes ? classes.header2 : ''}
        style={styles}
      >
        {children}
      </h2>
    ),
    [BLOCKS.HEADING_3]: (n: any, children: any) => (
      <h3 id={headingId(n)} key={`heading3-${children}`} style={styles}>
        {children}
      </h3>
    ),
    [BLOCKS.PARAGRAPH]: (_n: any, children: any) => (
      <p
        key={`paragraph-${children}`}
        className={classes ? classes.paragraph : ''}
        style={styles}
      >
        {children}
      </p>
    ),
    [BLOCKS.QUOTE]: (_n: any, children: any) => (
      <div
        key={`quote-${children}`}
        className={classes ? classes.quote : ''}
        style={styles}
      >
        {children}
      </div>
    ),
    [BLOCKS.UL_LIST]: (_n: any, children: any) => (
      <ul
        key={`ulList-${children}`}
        className={classes ? classes.list : ''}
        style={styles}
      >
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_n: any, children: any) => (
      <ol
        key={`olList-${children}`}
        className={classes ? classes.orderedList : ''}
        style={styles}
      >
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (_n: any, children: any) => (
      <li
        key={`listItem-${children}`}
        className={classes ? classes.listItem : ''}
        style={styles}
      >
        {children}
      </li>
    ),
    [BLOCKS.HR]: () => (
      <hr
        key={`hr-${Math.random()}`}
        className={classes ? classes.hr : ''}
        style={styles}
      />
    ),
    [BLOCKS.TABLE]: (_n: any, children: any) => (
      <table className={classes ? classes.table : ''}>
        <tbody>{children}</tbody>
      </table>
    ),
    [BLOCKS.TABLE_ROW]: (_n: any, children: any) => (
      <tr className={classes ? classes.tableRow : ''}>{children}</tr>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (_n: any, children: any) => (
      <th className={classes ? classes.tableHeaderCell : ''}>{children}</th>
    ),
    [BLOCKS.TABLE_CELL]: (_n: any, children: any) => (
      <td className={classes ? classes.tableCell : ''}>{children}</td>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (n: any) => getImage(n),
    [INLINES.HYPERLINK]: (n: any, children: any) => {
      const { data } = n;
      const href = data ? data.uri : '';
      const props = href.charAt(0) !== '#' ? EMAIL_LINK_PROPS : {};

      return (
        <a
          className={classes ? classes.link : ''}
          href={href}
          key={`link-${children}`}
          {...props}
          {...(linkAnalytics || {})}
          style={styles}
        >
          {children}
        </a>
      );
    }
  };

  const options = {
    renderMark: {},
    renderNode,
    renderText: (txt: string) =>
      txt
        .replace(/\\n/g, '\n')
        .split('\n')
        .flatMap((t, i) => [i > 0 && <br key={i} />, t])
  };

  if (classes && classes.bold) {
    options.renderMark = {
      [MARKS.BOLD]: (txt: string) => (
        <b key={`bold-${txt}`} className={classes.bold}>
          {txt}
        </b>
      )
    };
  }

  return documentToReactComponents(text.json, options);
}
